import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import Card from 'components/inputs/Card';
import { MdClose } from 'react-icons/md';
import { Row, Col } from 'react-bootstrap';
import { Formik, Form, FieldArray } from 'formik';
import TextInput from 'components/inputs/Input';
import Button from 'components/inputs/Button';
import _ from 'lodash';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { CREATE_VEHICLE_MODEL, UPDATE_VEHICLE_MODEL } from 'actions/vehicleModel';
import Select from 'components/inputs/Select';
import { FETCH_VEHICLE_MAKE } from 'actions/vehicleMake';
import { CREATE_STATUS_CODE, UPDATE_STATUS_CODE } from 'components/common/constant';
import axios from 'axios';
import { cookie } from 'utils/cookies/cookies';
import { toast } from 'react-toastify';
import queryString from 'query-string';
import { useTranslation } from 'react-i18next';
import CheckBox from 'components/inputs/CheckBox';
import Avatar from 'components/inputs/Avatar';
import { FETCH_CONNECTOR_TYPE } from 'actions/connectorType';

const ModelForm = (props) => {
  const { isEdit, onClose = _.noop() } = props;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const token = cookie.get('token');
  const modelDetail = useSelector((state) => state.vehicleModel.vehicleModelDetail);
  const makeDetail = useSelector((state) => state.vehicleMake.vehicleMakes);
  const makePage = useSelector((state) => state.vehicleMake.page);
  const makeTotalPage = useSelector((state) => state.vehicleMake.totalPages);
  const isLoading = useSelector((state) => state.vehicleModel.isLoading);
  const connectorTypeList = useSelector((state) => state.connectorType.connectorTypes);
  const [searchText] = useState('');

  const addVehicleModel = useCallback((data) => {
    dispatch({
      type: CREATE_VEHICLE_MODEL.REQUEST,
      payload: data,
      cb: (res) => {
        if (_.get(res, 'status') === CREATE_STATUS_CODE) {
          onClose();
        }
      },
    });
  }, []);

  const updateVehicleModel = useCallback((data) => {
    dispatch({
      type: UPDATE_VEHICLE_MODEL.REQUEST,
      payload: data,
      cb: (res) => {
        if (_.get(res, 'status') === UPDATE_STATUS_CODE) {
          onClose();
        }
      },
    });
  }, []);

  const getAllVehicleMake = useCallback((data = {}) => {
    dispatch({ type: FETCH_VEHICLE_MAKE.REQUEST, payload: { data, limit: 999 } });
  }, []);

  const getConnectorType = useCallback(
    (data = {}) => {
      if (searchText) {
        const searchconnectorTypeList = {
          ...data,
          search: searchText,
          limit: 9999,
        };
        dispatch({
          type: FETCH_CONNECTOR_TYPE.REQUEST,
          payload: searchconnectorTypeList,
        });
      } else {
        data = { ...data, limit: 9999 };
        dispatch({
          type: FETCH_CONNECTOR_TYPE.REQUEST,
          payload: data,
        });
      }
    },
    [searchText]
  );

  const handleImage = (image, savePath, setFieldValue) => {
    const fileName = _.get(image, 'name', '');

    axios({
      method: 'get',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      url: process.env.REACT_APP_API_URL + `/util/fileupload?name=${fileName}`,
    })
      .then((res) => {
        if (res.status === 201) {
          const signedRequest = _.get(res, 'data.signedRequest', '');
          handleUploadFile(signedRequest, image, savePath, setFieldValue);
        } else {
          toast.error(res.data.message);
        }
      })
      .catch((e) => console.error('Error -> ', e));
  };

  const handleUploadFile = (url, imageData, savePath, setFieldValue) => {
    const fileType = _.get(imageData, 'type', '');

    axios({
      method: 'put',
      headers: {
        'Content-Type': fileType,
        'Content-Encoding': 'base64',
      },
      url: url,
      data: imageData,
    })
      .then((res) => {
        if (res.status === 200) {
          const imgUrlData = queryString.parseUrl(url);
          setFieldValue(savePath, imgUrlData.url);
        } else {
          toast.error(res.data.message);
        }
      })
      .catch((e) => console.error('Error -> ', e));
  };

  const ModelSchema = Yup.object().shape({
    makeId: Yup.string().required(t('modelForm.makeId')),
    connector_type: Yup.string().required(t('connectorTypeForm.select')),
    type: Yup.string().required(t('modelForm.type')),
    model: Yup.string()
      .required(t('modelForm.model'))
      .strict(true)
      .trim(t('modelForm.space'))
      .matches(/^[A-Za-z0-9\s]+$/, t('modelForm.matchNumAlpha'))
      .max(100, t('modelForm.maxlimit')),
    year: Yup.string()
      .required(t('modelForm.year'))
      .strict(true)
      .trim(t('modelForm.space'))
      .matches(/^[0-9]+$/, t('modelForm.matchesNumber'))
      .min(4, t('modelForm.formatYear'))
      .max(4, t('modelForm.formatYear')),
    // vin_num: Yup.string()
    //   .required('VIN number is required')
    //   .strict(true)
    //   .trim('Space is not allowed')
    //   .matches(/^[A-Za-z0-9]+$/, 'Only alphabets and numbers are allowed'),
    variant: Yup.string().required('Variant is required'),
    max_charging_capacity: Yup.string()
      .required('Battery capacity is required')
      .strict(true)
      .trim('Space is not allowed')
      .matches(/^(?:\d+|\d+\.\d+)$/, 'Only numbers are allowed'),
    picture: Yup.mixed().nullable().required('Model Picture is required'),
    max_distance_range: Yup.number().min(0).typeError('value must be a number').required('value is required'),
    max_charging_speed: Yup.number().min(0).typeError('value must be a number').required('value is required'),
    battery_capacities: Yup.array()
      .of(
        Yup.number()
          .typeError('Capacity must be a number')
          .required('Capacity is required')
          .min(1, 'Capacity must be greater than 0')
          .max(1000, 'Capacity must be less than or equal to 1000')
      )
      .required('At least one capacity is required'),
  });

  const initialValues = isEdit
    ? {
        ...modelDetail,
        type: _.get(modelDetail, 'makeId.type', ''),
        makeId: _.get(modelDetail, 'makeId.id', ''),
        connector_type: _.get(modelDetail, 'connector_type', ''),
        battery_capacities: (modelDetail?.battery_capacities || ['']).map((val) => val?.replace(/\s*kWh$/, '')),
      }
    : {
        makeId: '',
        connector_type: '',
        model: '',
        type: '',
        year: '',
        variant: '',
        // vin_num: '',
        max_charging_capacity: '',
        picture: '',
        is_dual_gun_supported: false,
        supports_auto_charge: true,
        max_distance_range: 0,
        max_charging_speed: 0,
        battery_capacities: [],
      };

  useEffect(() => {
    getAllVehicleMake();
    getConnectorType();
  }, []);

  return (
    <React.Fragment>
      <div className="model-form-page__main">
        <Card>
          <div className="model-form-header__block">
            <div className="model-header-name">
              <span>
                {isEdit ? 'Edit' : 'Add'} {t('modelForm.title')}
              </span>
            </div>
            <div className="close-btn">
              <MdClose size={30} color="#be210b" onClick={onClose} />
            </div>
          </div>
          <div className="model-form-body__block">
            <div className="model-form--block">
              <Formik
                enableReinitialize={!!isEdit}
                initialValues={initialValues}
                validationSchema={ModelSchema}
                onSubmit={(values, { setSubmitting }) => {
                  if (isEdit) {
                    const payload = {
                      ...values,
                      battery_capacities: values.battery_capacities.map((val) => (val ? `${val} kWh` : val)),
                    };
                    updateVehicleModel(payload);
                  } else {
                    const payload = {
                      ...values,
                      battery_capacities: values.battery_capacities.map((val) => (val ? `${val} kWh` : val)),
                    };
                    addVehicleModel(payload);
                  }
                  setSubmitting(false);
                }}
              >
                {({ values, errors, touched, handleSubmit, handleChange, setFieldValue, isSubmitting }) => (
                  <Form onSubmit={handleSubmit}>
                    <Row>
                      <Col lg={12} md={12}>
                        <Select
                          isRequired
                          label={t('modelForm.vehicleType')}
                          placeholder={t('placeHolder.selectVehicleType')}
                          options={[
                            { label: 'Bus', value: 'bus' },
                            { label: 'Truck', value: 'truck' },
                            { label: 'Car', value: 'car' },
                            { label: 'Two wheeler', value: 'two wheeler' },
                            { label: 'Three wheeler', value: 'three wheeler' },
                            { label: 'Other', value: 'other' },
                          ]}
                          name="type"
                          // value={values.type}
                          value={_.get(values, 'type', '')}
                          onChange={(val) => setFieldValue('type', val)}
                          error={errors.type && touched.type ? errors.type : null}
                        />
                      </Col>
                      <Col lg={12} md={12}>
                        <Select
                          isRequired
                          label={t('modelForm.oem')}
                          options={
                            values.type &&
                            _.map(
                              _.filter(makeDetail, (data) => data.type === values.type),
                              (item) => {
                                return { label: item.name, value: item.id };
                              }
                            )
                          }
                          onMenuScrollDown={true}
                          getDataOnScrollDown={getAllVehicleMake}
                          page={makePage}
                          totalPage={makeTotalPage}
                          placeholder={t('modelForm.makeId')}
                          value={_.get(values, 'makeId', '')}
                          name="makeId"
                          onChange={(val) => setFieldValue(`makeId`, val)}
                          error={errors.makeId && touched.makeId ? errors.makeId : null}
                        />
                      </Col>

                      <Col lg={12} md={12}>
                        <Select
                          isRequired
                          label={t('connectorTypeForm.title')}
                          options={_.map(_.uniqBy(connectorTypeList, 'id'), (item) => ({
                            value: item.id,
                            label: (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Avatar imgSrc={_.get(item, 'icon', '')} className="connectorTypeList-logo" />
                                <span>{_.get(item, 'name', '')}</span>
                              </div>
                            ),
                          }))}
                          onMenuScrollDown={true}
                          getDataOnScrollDown={getConnectorType}
                          page={makePage}
                          totalPage={makeTotalPage}
                          placeholder={t('connectorTypeForm.select')}
                          value={_.get(values, 'connector_type', '')}
                          name="connector_type"
                          onChange={(val) => setFieldValue('connector_type', val)}
                          error={errors.connector_type && touched.connector_type ? errors.connector_type : null}
                        />
                      </Col>

                      <Col lg={12} md={12}>
                        <TextInput
                          isRequired
                          label={t('modelForm.evModelName')}
                          placeholder={t('modelForm.evModelName')}
                          name="model"
                          value={_.get(values, 'model', '')}
                          onChange={handleChange}
                          error={errors.model && touched.model ? errors.model : null}
                        />
                      </Col>
                      <Col lg={12} md={12}>
                        <TextInput
                          isRequired
                          label={t('modelForm.variant')}
                          placeholder={t('modelForm.variant')}
                          name="variant"
                          value={_.get(values, 'variant', '')}
                          onChange={handleChange}
                          error={errors.variant && touched.variant ? errors.variant : null}
                        />
                      </Col>
                      <Col lg={6} md={12}>
                        <TextInput
                          isRequired
                          type="file"
                          label={t('modelForm.file')}
                          name="picture"
                          accept="image/*"
                          onChange={(e) => handleImage(e.currentTarget.files[0], 'picture', setFieldValue)}
                          error={errors.picture && touched.picture ? errors.picture : null}
                        />
                      </Col>
                      <Col lg={6} md={12}>
                        <Select
                          label={t('modelForm.priority')}
                          options={[
                            { label: '1', value: 1 },
                            { label: '2', value: 2 },
                            { label: '3', value: 3 },
                            { label: '4', value: 4 },
                            { label: '5', value: 5 },
                          ]}
                          onMenuScrollDown={true}
                          placeholder={t('modelForm.priority')}
                          value={_.get(values, 'priority', '')}
                          name="priority"
                          onChange={(val) => setFieldValue(`priority`, val)}
                        />
                      </Col>
                      <Col lg={6} md={12}>
                        <TextInput
                          isRequired
                          label={t('modelForm.yearLabel')}
                          placeholder={t('modelForm.yearFormatLabel')}
                          name="year"
                          value={values.year}
                          onChange={handleChange}
                          error={errors.year && touched.year ? errors.year : null}
                        />
                      </Col>
                      {/* <Col lg={6} md={12}>
                        <TextInput
                          isRequired
                          label="VIN number"
                          placeholder="VIN number"
                          name="vin_num"
                          value={values.vin_num}
                          onChange={handleChange}
                          error={errors.vin_num && touched.vin_num ? errors.vin_num : null}
                        />
                      </Col> */}
                      <Col lg={6} md={12}>
                        <TextInput
                          isRequired
                          post_text="kWh"
                          label={t('modelForm.batteryCapacity')}
                          placeholder={t('modelForm.batteryCapacity')}
                          name="max_charging_capacity"
                          value={values.max_charging_capacity}
                          onChange={(e) => {
                            handleChange(e);
                            const val = parseFloat(e.target.value) || 0;
                            if (values.battery_capacities.length === 0) {
                              setFieldValue('battery_capacities', [val]);
                            } else {
                              setFieldValue('battery_capacities[0]', val);
                            }
                          }}
                          error={errors.max_charging_capacity && touched.max_charging_capacity ? errors.max_charging_capacity : null}
                        />
                      </Col>
                      <Col lg={6} md={12}>
                        <TextInput
                          post_text="km"
                          type="number"
                          label={t('modelForm.max_distance_range')}
                          placeholder={t('modelForm.max_distance_range')}
                          name="max_distance_range"
                          value={values.max_distance_range}
                          onChange={handleChange}
                          error={errors.max_distance_range && touched.max_distance_range ? errors.max_distance_range : null}
                        />
                      </Col>
                      <Col lg={6} md={12}>
                        <TextInput
                          post_text="kW"
                          type="number"
                          label={t('modelForm.max_charging_speed')}
                          placeholder={t('modelForm.max_charging_speed')}
                          name="max_charging_speed"
                          value={values.max_charging_speed}
                          onChange={handleChange}
                          error={errors.max_charging_speed && touched.max_charging_speed ? errors.max_charging_speed : null}
                        />
                      </Col>
                      <Col lg={'auto'}>
                        <div className="dual-gun-supported-field-main--block">
                          <div className="dual-gun-supported-checkbox-input__block">
                            <label htmlFor="is_dual_gun_supported" className="dual-gun-supported--label">
                              {t('modelForm.dual_gun_supported')}
                            </label>
                            <CheckBox
                              name="is_dual_gun_supported"
                              checked={values.is_dual_gun_supported}
                              onChange={(e) => setFieldValue('is_dual_gun_supported', e.target.checked)}
                            />
                          </div>
                        </div>
                      </Col>
                      <Col lg={'auto'}>
                        <div className="auto-charge-supported-field-main--block">
                          <div className="auto-charge-supported-checkbox-input__block">
                            <label htmlFor="supports_auto_charge" className="auto-charge-supported--label">
                              {t('modelForm.supports_auto_charge')}
                            </label>
                            <CheckBox
                              name="supports_auto_charge"
                              checked={values.supports_auto_charge}
                              onChange={(e) => setFieldValue('supports_auto_charge', e.target.checked)}
                            />
                          </div>
                        </div>
                      </Col>
                      <Col lg={12} md={12} sm={12}>
                        <label className="form-label">{t('modelForm.supportbatteryCapacity')}</label>
                        <FieldArray name="battery_capacities">
                          {({ push, remove }) => (
                            <div>
                              {values?.battery_capacities?.map((cap, index) => (
                                <div key={index} style={{ position: 'relative', marginBottom: '20px', marginTop: '20px' }}>
                                  <TextInput
                                    post_text="kWh"
                                    type="number"
                                    name={`battery_capacities[${index}]`}
                                    placeholder="Enter capacity"
                                    value={values.battery_capacities[index]}
                                    onChange={(e) => {
                                      let val = parseFloat(e.target.value);
                                      setFieldValue(`battery_capacities[${index}]`, val);
                                    }}
                                    error={
                                      errors.battery_capacities &&
                                      errors.battery_capacities[index] &&
                                      touched.battery_capacities &&
                                      touched.battery_capacities[index]
                                        ? errors.battery_capacities[index]
                                        : null
                                    }
                                  />

                                  {values.battery_capacities.length > 1 && (
                                    <MdClose
                                      style={{
                                        position: 'absolute',
                                        right: '-25px',
                                        bottom: '20px',
                                        cursor: 'pointer',
                                      }}
                                      onClick={() => {
                                        if (values.battery_capacities.length > 1) {
                                          remove(index);
                                        } else {
                                          setFieldValue(`battery_capacities[0]`, '');
                                        }
                                      }}
                                    />
                                  )}
                                </div>
                              ))}

                              <Button type="button" onClick={() => push('')}>
                                + Add Capacity
                              </Button>
                            </div>
                          )}
                        </FieldArray>
                      </Col>
                    </Row>
                    <div className="form_btn--block">
                      <Button className="form--btn cancel_btn" onClick={onClose}>
                        {t('button.cancel')}
                      </Button>
                      <Button type="submit" disabled={isSubmitting || isLoading} className="form--btn">
                        {isEdit ? 'Update' : 'Create'}
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </Card>
      </div>
    </React.Fragment>
  );
};
ModelForm.propTypes = {
  onSuccess: PropTypes.func,
  onClose: PropTypes.func,
  isEdit: PropTypes.bool,
};
export default ModelForm;
